/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 21-07-2026
 *
 * Root-service voor de weergave-instellingen beginnersmodus en tekstbeschrijving.
 * De instellingen zijn een combinatie van een projectvlag (mag het in dit project?) en een
 * opgeslagen gebruikersvoorkeur (staat de toggle aan?).
 *
 * Sinds golf 4e leest de service de projectvlaggen zelf uit de `SessieService` en haalt hij de
 * voorkeuren zelf op zodra er iemand is ingelogd; de `initialiseer` vanuit AngularJS is vervallen.
 */
import {computed, effect, inject, Injectable, signal, untracked} from '@angular/core';
import {ApiService} from '../app/backend/api.service';
import {SessieService} from './sessie.service';

/**
 * Gebruikersvoorkeuren zoals opgeslagen in de userdata onder de key 'modelview'. Het backend
 * vervangt bij `user.saveData` de hele waarde onder die key, dus bij opslaan gaan altijd beide
 * voorkeuren mee — een enkele gewijzigde voorkeur zou de andere wissen (bevinding golf a2 stap 2,
 * test 1: tekstbeschrijving uit, beginnersmodus toggelen, herladen → de tekstbeschrijving stond
 * weer aan). Bij het lezen mogen ze ontbreken (oudere opslag); dan geldt de default.
 */
export interface WeergaveVoorkeuren {
    beginnersmodus?: boolean;
    tekstbeschrijving?: boolean;
}

/**
 * Onder welke sleutel de voorkeuren bij de gebruiker bewaard worden.
 */
const OPSLAGSLEUTEL = 'modelview';

@Injectable({providedIn: 'root'})
export class WeergaveInstellingenService {

    /**
     * De sessie: bron van de twee projectvlaggen.
     */
    private readonly sessie = inject(SessieService);

    /**
     * De backendlaag, voor het ophalen en opslaan van de voorkeuren bij de gebruiker.
     */
    private readonly api = inject(ApiService);

    /**
     * Projectvlag: is beginnersmodus beschikbaar in dit project?
     */
    private readonly projectBeginnersmodus = computed(() => !!this.sessie.projectdata()?.beginnersmodus);

    /**
     * Projectvlag: is de tekstbeschrijving beschikbaar in dit project?
     */
    private readonly projectTekstbeschrijving = computed(() => !!this.sessie.projectdata()?.tekstbeschrijving);

    /**
     * Gebruikersvoorkeur voor de beginnersmodus (standaard aan).
     */
    private readonly voorkeurBeginnersmodus = signal(true);

    /**
     * Gebruikersvoorkeur voor de tekstbeschrijving (standaard aan).
     */
    private readonly voorkeurTekstbeschrijving = signal(true);

    /**
     * Voor welke gebruiker de voorkeuren al opgehaald zijn; voorkomt dat een projectwissel (die de
     * userdata vervangt) ze nog een keer vraagt.
     */
    private gehaaldVoor: string | null = null;

    /**
     * Staat de beginnersmodus aan: projectvlag én gebruikersvoorkeur.
     */
    readonly beginnersmodusAan = computed(() => this.projectBeginnersmodus() && this.voorkeurBeginnersmodus());

    /**
     * Staat de tekstbeschrijving aan: projectvlag én gebruikersvoorkeur.
     * De model-leeg-check ("geen beschrijving bij een leeg model") is geen instelling en blijft
     * bij de consument.
     */
    readonly tekstbeschrijvingAan = computed(() => this.projectTekstbeschrijving() && this.voorkeurTekstbeschrijving());

    constructor() {
        //zodra er iemand is ingelogd, staan zijn voorkeuren klaar om opgehaald te worden. Het ophalen
        //zelf staat in `untracked`: anders zou het effect ook de userdata gaan volgen en bij elke
        //wijziging daarvan opnieuw vragen
        effect(() => {
            const naam = this.sessie.naam();
            if (!this.sessie.ingelogd() || !naam || this.gehaaldVoor === naam) {
                return;
            }
            this.gehaaldVoor = naam;
            untracked(() => void this.haalVoorkeuren());
        });
    }

    /**
     * Zet de opgeslagen gebruikersvoorkeuren; ontbrekende voorkeuren behouden hun default.
     */
    zetVoorkeuren(voorkeuren: WeergaveVoorkeuren | false | undefined): void {
        if (!voorkeuren) {
            return;
        }
        if (voorkeuren.beginnersmodus !== undefined) {
            this.voorkeurBeginnersmodus.set(voorkeuren.beginnersmodus);
        }
        if (voorkeuren.tekstbeschrijving !== undefined) {
            this.voorkeurTekstbeschrijving.set(voorkeuren.tekstbeschrijving);
        }
    }

    /**
     * Zet de gebruikersvoorkeur voor de beginnersmodus om en slaat de voorkeuren op.
     */
    toggleBeginnersmodus(): void {
        this.voorkeurBeginnersmodus.update(aan => !aan);
        this.slaVoorkeurenOp();
    }

    /**
     * Zet de gebruikersvoorkeur voor de tekstbeschrijving om en slaat de voorkeuren op.
     */
    toggleTekstbeschrijving(): void {
        this.voorkeurTekstbeschrijving.update(aan => !aan);
        this.slaVoorkeurenOp();
    }

    /**
     * Haalt de bewaarde voorkeuren van de ingelogde gebruiker op. Een mislukte call laat de defaults
     * staan: een weergavevoorkeur is geen reden om de gebruiker een foutmelding te geven.
     */
    private async haalVoorkeuren(): Promise<void> {
        try {
            this.zetVoorkeuren(await this.api.haalUserData(OPSLAGSLEUTEL));
        } catch (_fout) {
            this.gehaaldVoor = null; //bij een volgende login mag het opnieuw
        }
    }

    /**
     * Slaat beide voorkeuren op; zie `WeergaveVoorkeuren` voor waarom dat altijd samen gaat.
     */
    private slaVoorkeurenOp(): void {
        void this.api.saveUserData(OPSLAGSLEUTEL, {
            beginnersmodus: this.voorkeurBeginnersmodus(),
            tekstbeschrijving: this.voorkeurTekstbeschrijving()
        }).catch(() => undefined); //een mislukte opslag mag geen unhandled rejection worden
    }
}
